import { type ReactNode, createContext, useContext, useEffect, useState } from "react";

export type ViewMode = "2d" | "3d";

const STORAGE_KEY = "cellbrary:view-mode";
const DEFAULT_MODE: ViewMode = "2d";

function readStored(): ViewMode {
  if (typeof window === "undefined") return DEFAULT_MODE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === "3d" || raw === "2d" ? raw : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}

function writeStored(mode: ViewMode): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // private mode, quota errors — non-fatal
  }
}

interface ViewModeContextValue {
  mode: ViewMode;
  setMode: (m: ViewMode) => void;
}

const ViewModeContext = createContext<ViewModeContextValue | null>(null);

/**
 * Wrap the app once; every useViewMode() call below shares the same state.
 * Without this, multiple useState instances drift out of sync — clicking the
 * toggle in the Header updates the toggle's local state but Landing's local
 * state stays at the previous value until Landing re-mounts.
 */
export function ViewModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ViewMode>(readStored);

  useEffect(() => {
    const handler = (e: StorageEvent): void => {
      if (e.key !== STORAGE_KEY) return;
      if (e.newValue === "2d" || e.newValue === "3d") {
        setMode(e.newValue);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const update = (next: ViewMode): void => {
    writeStored(next);
    setMode(next);
  };

  return (
    <ViewModeContext.Provider value={{ mode, setMode: update }}>
      {children}
    </ViewModeContext.Provider>
  );
}

export function useViewMode(): [ViewMode, (mode: ViewMode) => void] {
  const ctx = useContext(ViewModeContext);
  if (!ctx) {
    throw new Error("useViewMode must be used inside <ViewModeProvider>");
  }
  return [ctx.mode, ctx.setMode];
}

export function isWebGL2Available(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2"));
  } catch {
    return false;
  }
}
